import { create } from "zustand";
import { persist } from "zustand/middleware";
import { browserJSONStorage } from "@/lib/persist-storage";

export interface AnnouncementData {
  text: string;
  link?: string;
  ctaLabel?: string;
  active: boolean;
  dismissible?: boolean;
  /** Layout & style (see lib/announcement.ts). */
  theme?: "sage" | "cream" | "dark" | "custom";
  bgColor?: string;
  textColor?: string;
  align?: "left" | "center";
  icon?: string;
  /** Pop-up hero image (R2 key, /asset path, or absolute URL). */
  imageKey?: string;
  showBar?: boolean;
  showDialog?: boolean;
}

/**
 * Dismissal — permanent, so it lives in localStorage. `AnnouncementBar` used to
 * ALSO hand-roll `localStorage["ann-dismissed-<hash>"] = "1"` on every dismiss,
 * which is what actually made dismissal survive a tab close while this store
 * only used sessionStorage. Both writers are now this one key: a component must
 * never hand-roll `setItem` (`.claude/rules/state-management.md`).
 */
export const ANNOUNCEMENT_STORAGE_KEY = "floral-muse-announcement";

/**
 * "The pop-up already opened" — per TAB SESSION, so sessionStorage. Replaces the
 * component's second hand-rolled key, `sessionStorage["ann-dialog-<hash>"]`.
 */
export const ANNOUNCEMENT_DIALOG_STORAGE_KEY = "floral-muse-announcement-dialog";

interface AnnouncementState {
  data: AnnouncementData | null;
  /**
   * THE single stored truth about dismissal: the hash of the announcement text
   * the visitor dismissed, or null.
   *
   * ⚠ There used to be a `dismissed: boolean` beside this, and it was a pure
   * function of it (`dismissedHash !== null`). Two fields, two writers, and a
   * persisted `{dismissed: true, dismissedHash: null}` blob hid the announcement
   * bar FOREVER with no way for an admin to bring it back. Derive instead —
   * {@link isAnnouncementDismissed} — and note it compares against the CURRENT
   * announcement's hash, so changing the copy re-shows the bar by construction
   * rather than by a "reset when the hash differs" branch in the component.
   */
  dismissedHash: number | null;
  setData: (data: AnnouncementData | null) => void;
  dismiss: (hash: number) => void;
  reset: () => void;
}

export function hashCode(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/** Only the dismissal survives a reload; fetched `data` never does. */
type AnnouncementPersisted = { dismissedHash: number | null };

/**
 * Is THIS announcement dismissed? Derived at the point of use, never stored.
 *
 * `data` is a parameter rather than being read off the store because the bar
 * falls back to the server-rendered `initial` prop until the client fetch lands,
 * so the announcement in view is not always the one in the store.
 */
export function isAnnouncementDismissed(
  dismissedHash: number | null,
  data: AnnouncementData | null,
): boolean {
  if (!data || dismissedHash === null) return false;
  return dismissedHash === hashCode(data.text);
}

export const useAnnouncement = create<AnnouncementState>()(
  persist(
    (set) => ({
      data: null,
      dismissedHash: null,

      setData: (data) => set({ data }),

      dismiss: (hash) => set({ dismissedHash: hash }),

      reset: () => set({ data: null, dismissedHash: null }),
    }),
    {
      name: ANNOUNCEMENT_STORAGE_KEY,
      storage: browserJSONStorage("local"),
      version: 1,
      /**
       * v0 persisted `{ dismissed, dismissedHash }` — the mirror. Keep the hash,
       * drop the boolean; a v0 blob that said `dismissed: true` with a null hash
       * was the permanently-hidden-bar bug, and dropping the boolean is what
       * heals it. (`version` and `migrate` always ship together: a bump without
       * one makes zustand `console.error` then destructure `undefined`, leaving
       * the store on its defaults forever.)
       */
      migrate: (persisted) => {
        const previous = (persisted ?? {}) as Record<string, unknown>;
        return {
          dismissedHash:
            typeof previous.dismissedHash === "number"
              ? previous.dismissedHash
              : null,
        } satisfies AnnouncementPersisted;
      },
      // Only persist the dismissal, never the fetched announcement.
      partialize: (state): AnnouncementPersisted => ({
        dismissedHash: state.dismissedHash,
      }),
    },
  ),
);

interface AnnouncementSessionState {
  /**
   * Hash of the announcement whose pop-up has already opened in this tab
   * session, or null. Session-scoped on purpose: the pop-up is a once-per-visit
   * interruption, not a permanent one.
   */
  dialogSeenHash: number | null;
  markDialogSeen: (hash: number) => void;
  reset: () => void;
}

export const useAnnouncementSession = create<AnnouncementSessionState>()(
  persist(
    (set) => ({
      dialogSeenHash: null,

      markDialogSeen: (hash) => set({ dialogSeenHash: hash }),

      reset: () => set({ dialogSeenHash: null }),
    }),
    {
      name: ANNOUNCEMENT_DIALOG_STORAGE_KEY,
      storage: browserJSONStorage("session"),
      partialize: (state) => ({ dialogSeenHash: state.dialogSeenHash }),
    },
  ),
);
